interface ReportChild {
    id: string;
    [key: string]: unknown;
}

interface ReportCheck {
    layer_id: string;
    location?: string;
    status?: string;
}

const normalizeStatus = (status: unknown): string => (typeof status === "string" ? status.replace("flag-", "") : "");

// * The status a pallet page is filtered on: the pallet's `{location}_status` for the check type
// * shown on that page (outbound for the outbound report, returns for returns, the fallback
// * check type when the page shows a fallback check), else the status of the check itself.
// * Both already hold the manual flag when an inspector overruled the business rules.
// * Never a fixed check type: an outbound report must not filter on the intake status.
export const getReportViewStatus = (child: ReportChild | undefined, check: Partial<ReportCheck> | undefined): string => {
    const locationStatus = check?.location ? child?.[`${check.location}_status`] : undefined;
    return normalizeStatus(locationStatus) || normalizeStatus(check?.status);
};

export const filterChecksByReportView = <T extends ReportCheck>(
    checks: T[],
    children: ReportChild[],
    viewType: string
): T[] => {
    if (!viewType || viewType === "full") return checks;
    if (viewType === "summary") return [];

    const includedStatuses = new Set(viewType.split(","));
    const childrenById = new Map(children.map((child) => [child.id, child]));

    return checks.filter((check) => {
        const child = childrenById.get(check.layer_id);
        // * Checks that do not belong to a report child are never pallet pages of this report
        return !!child && includedStatuses.has(getReportViewStatus(child, check));
    });
};

interface ReportRenderUnit {
    layer?: { id?: string };
    latestCheck?: Partial<ReportCheck>;
}

// Pages for the report layer itself (e.g. the order overview) stay in every view;
// only pallet pages are filtered, matching the general intake template.
export const filterRenderUnitsByReportView = <T extends ReportRenderUnit>(
    units: T[],
    children: ReportChild[],
    viewType: string,
    reportLayerId: string
): T[] => {
    if (!viewType || viewType === "full") return units;

    const isReportLayerUnit = (unit: T) => !unit.layer?.id || unit.layer.id === reportLayerId;
    const childUnits = units
        .filter((unit) => !isReportLayerUnit(unit))
        .map((unit) => ({
            unit,
            layer_id: unit.layer?.id as string,
            location: unit.latestCheck?.location,
            status: unit.latestCheck?.status,
        }));
    const includedUnits = new Set(filterChecksByReportView(childUnits, children, viewType).map(({ unit }) => unit));

    return units.filter((unit) => isReportLayerUnit(unit) || includedUnits.has(unit));
};
